import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import { CustomerRules } from './models/CustomerInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

interface CustomerProps {
    id: number;
    systemCompanyId: number;
    readOnly: boolean;
    accountTypeId: number;
    customerRule: CustomerRules;
    errors: { [key: string]: string };
    updateRulesString(fieldName: string, fieldValue: string): void;
    updateRulesNumber(fieldName: string, fieldValue: number): void;
    updateRulesChecked(fieldName: string, fieldValue: boolean): void;
    updateErrors(errors: { [key: string]: string }): void;
}

const CustomerRule = (props: CustomerProps) => {
    const [loading, setLoading] = React.useState(true);
    const [shutdownPeriodTypes, setShutdownPeriodTypes] = React.useState<GenericList[]>([]);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, [props.id]);


    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Customer/GetShutdownPeriodTypes', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setShutdownPeriodTypes(res.data);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const updateErrors = (errorName: string) => {
        //check if there is a value and an error, and if so remove from error list
        var error = props.errors;
        delete error[errorName];
        props.updateErrors(error);
    }

    const handleCheckboxChange = (e: any) => {
        props.updateRulesChecked(e.target.name, e.target.checked);
    }

    const handleStringChange = (e: any) => {
        updateErrors(e.target.name);
        props.updateRulesString(e.target.name, e.target.value);
    }

    const handleNumberChange = (e: any) => {
        updateErrors(e.target.name);
        props.updateRulesNumber(e.target.name, e.target.value * 1);
    }

    const renderDetails = (
        <div>
            <label className="input-group" htmlFor="gateCode">
                <span className="label label-large">Gate Code</span>
                <input className="input" type="text" id="gateCode" name="gateCode" value={props.customerRule.gateCode} onChange={(e) => handleStringChange(e)}></input>
            </label>

            <label className={props.systemCompanyId === 3 ? "input-group" : "hidden"} htmlFor="sendConfirmationEmail">
                <span className="label label-large">Send Confirmation Email</span>
                <input className="checkbox" type="checkbox" id="sendConfirmationEmail" name="sendConfirmationEmail" checked={props.customerRule.sendConfirmationEmail} onClick={(e) => handleCheckboxChange(e)}></input>
            </label>

            <label className={props.systemCompanyId === 3 ? "hidden" : "input-group"} htmlFor="createScaffoldAudit">
                <span className="label label-large">Create Scaffold Audits</span>
                <input className="checkbox" type="checkbox" id="createScaffoldAudit" name="createScaffoldAudit" checked={props.customerRule.createScaffoldAudit} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
            </label>

            <label className="input-group" htmlFor="allowOnGoingHire">
                <span className="label label-large">Allow On-going Hire</span>
                <input className="checkbox" type="checkbox" id="allowOnGoingHire" name="allowOnGoingHire" checked={props.customerRule.allowOnGoingHire} onClick={(e) => handleCheckboxChange(e)} disabled={props.systemCompanyId === 3 || props.readOnly}></input>
            </label>
            
            <div className={props.customerRule.allowOnGoingHire ? "" : "hidden"}>
                <label className={props.accountTypeId === 3 ? "input-group" : "hidden"} htmlFor="requireOverhireNewPo">
                    <span className="label label-large">Require New PO for on-going hire invoices?</span>
                    <input className="checkbox" type="checkbox" id="requireOverhireNewPo" name="requireOverhireNewPo" checked={props.customerRule.requireOverhireNewPo} onClick={(e) => handleCheckboxChange(e)}></input>
                </label>

                <label className="input-group" htmlFor="invoicePeriod">
                    <span className="label label-large">Overhire Invoice Period (Days)</span>
                    <input className="input customer__invDays" type="number" min="0" step="1" id="invoicePeriod" name="invoicePeriod" value={props.customerRule.invoicePeriod} onChange={(e) => handleNumberChange(e)} disabled={props.readOnly}></input>
                </label>
                <span className="label errors">{props.errors["invoicePeriod"]}</span>

                <label className={props.systemCompanyId === 3 ? "hidden" : "input-group"} htmlFor="invoicePeriodVoid">
                    <span className="label label-large">Void Invoice Period (Days)</span>
                    <input className="input customer__invDays" type="number" min="0" step="1" id="invoicePeriodVoid" name="invoicePeriodVoid" value={props.customerRule.invoicePeriodVoid} onChange={(e) => handleNumberChange(e)} disabled={props.readOnly}></input>
                </label>

                <label className="input-group" htmlFor="overhireRateDiscountPercentage">
                    <span className="label label-large">Overhire Rate Discount %</span>
                    <input className="input customer__invDays" type="number" min="0" step="1" id="overhireRateDiscountPercentage" name="overhireRateDiscountPercentage" value={props.customerRule.overhireRateDiscountPercentage} onChange={(e) => handleNumberChange(e)} disabled={props.readOnly}></input>
                </label>
                <span className="label errors">{props.errors["overhireRateDiscountPercentage"]}</span>
            </div>
            
            <label className="input-group" htmlFor="splitInvoiceByPo">
                <span className="label label-large">Split Invoice by PO</span>
                <input className="checkbox" type="checkbox" id="splitInvoiceByPo" name="splitInvoiceByPo" checked={props.customerRule.splitInvoiceByPo} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
            </label>

            <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                <label className="input-group" htmlFor="invoiceToBePrinted">
                    <span className="label label-large">Invoice - To Be Printed</span>
                    <input className="checkbox" type="checkbox" id="invoiceToBePrinted" name="invoiceToBePrinted" checked={props.customerRule.invoiceToBePrinted} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
                </label>
                <label className="input-group" htmlFor="eventInvoiceOnDelivery">
                    <span className="label label-large">Event Invoice Upon Delivery</span>
                    <input className="checkbox" type="checkbox" id="eventInvoiceOnDelivery" name="eventInvoiceOnDelivery" checked={props.customerRule.eventInvoiceOnDelivery} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
                </label>
                <label className="input-group" htmlFor="invoiceQuotedAmount">
                    <span className="label label-large">Do NOT create Overhire Invoices</span>
                    <input className="checkbox" type="checkbox" id="invoiceQuotedAmount" name="invoiceQuotedAmount" checked={props.customerRule.invoiceQuotedAmount} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
                </label>
                <label className="input-group" htmlFor="epaTransportCertificateCheck">
                    <span className="label label-large">MUST check EPA Transport Certificate</span>
                    <input className="checkbox" type="checkbox" id="epaTransportCertificateCheck" name="epaTransportCertificateCheck" checked={props.customerRule.epaTransportCertificateCheck} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
                </label>
            </div>

            <div className={props.systemCompanyId === 3 ? "hidden" : ""}>
                <label className="input-group" htmlFor="publicHolidayCharge">
                    <span className="label label-large">Charge Public Holidays</span>
                    <input className="checkbox" type="checkbox" id="publicHolidayCharge" name="publicHolidayCharge" checked={props.customerRule.publicHolidayCharge} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
                </label>
            </div>
            
            <div className={props.systemCompanyId === 3 ? "hidden" : ""}>
                <label className="input-group" htmlFor="shutdownPeriodType">
                    <span className="label label-large">Charge Shutdown Period</span>
                    <select className="select" id="shutdownPeriodType" name="shutdownPeriodType" value={props.customerRule.shutdownPeriodType} onChange={(e) => handleNumberChange(e)} disabled={props.readOnly}>
                        <option hidden defaultValue="-1"></option>
                        {shutdownPeriodTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <span className="label errors">{props.errors["shutdownPeriodType"]}</span>
            </div>
            <label className="input-group" htmlFor="chargeFuelSurcharge">
                <span className="label label-large">Charge Fuel Surcharge</span>
                <input className="checkbox" type="checkbox" id="chargeFuelSurcharge" name="chargeFuelSurcharge" checked={props.customerRule.chargeFuelSurcharge} onClick={(e) => handleCheckboxChange(e)} disabled={props.readOnly}></input>
            </label>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default CustomerRule;


